import React, { useEffect, useState } from 'react';
import { Loader2, Trophy, PartyPopper, AlertCircle } from 'lucide-react';
import { jobsApi } from '../services/api';
import { format } from 'date-fns';

const formatINR = (n) => {
  if (!n || n <= 0) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

const initials = (name) => {
  const parts = (name || '').trim().split(/\s+/);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
};

/**
 * "Closures so far" mini-strip — shown inside the Job View dialog.
 * Lists each candidate who has hit JOINED for this job, with offered CTC,
 * the recruiter who closed, and the closure date. When the job is fully
 * filled, the strip turns into a celebratory wall.
 */
const ClosuresStrip = ({ jobId, headcount, headcountFilled }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!jobId) return undefined;
    setLoading(true);
    jobsApi
      .getClosures(jobId)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-slate-500 text-xs">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading closures…
      </div>
    );
  }

  const items = data?.items || [];
  const totalCtc = data?.total_ctc || 0;
  const filled = headcountFilled ?? items.length;
  const target = headcount ?? 1;
  const isCelebration = target > 0 && filled >= target;

  return (
    <div data-testid="job-closures-strip">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-slate-900 flex items-center gap-1.5">
            {isCelebration ? (
              <PartyPopper className="w-4 h-4 text-amber-500" />
            ) : (
              <Trophy className="w-4 h-4 text-teal-600" />
            )}
            Closures so far
          </h4>
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
              isCelebration
                ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                : 'bg-slate-100 text-slate-700 border-slate-200'
            }`}
          >
            {filled}/{target}
          </span>
        </div>
        {totalCtc > 0 && (
          <span
            className="text-xs text-slate-500"
            data-testid="closures-total-ctc"
          >
            Total CTC closed:{' '}
            <span className="text-slate-900 font-semibold">
              {formatINR(totalCtc)}
            </span>
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          No closures yet — once a candidate hits the <em>Joined</em> stage,
          they'll show up here.
        </div>
      ) : (
        <>
          {isCelebration && (
            <div
              className="mb-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium flex items-center gap-2"
              data-testid="closures-celebration-banner"
            >
              <PartyPopper className="w-3.5 h-3.5" />
              All vacancies filled — pop the bubbly! 🎉
            </div>
          )}
          <ul className="space-y-1.5">
            {items.map((c) => (
              <li
                key={c.application_id}
                className={`flex items-center gap-3 rounded-md border px-3 py-2 ${
                  isCelebration
                    ? 'bg-emerald-50/40 border-emerald-200/70'
                    : 'bg-white border-slate-200'
                }`}
                data-testid={`closure-row-${c.application_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 border border-teal-200 flex items-center justify-center text-[11px] font-semibold shrink-0">
                  {initials(c.candidate_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {c.candidate_name}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    Closed by {c.recruiter_name || '—'}
                    {c.joined_at && (
                      <>
                        {' '}
                        · joined {format(new Date(c.joined_at), 'MMM dd, yyyy')}
                      </>
                    )}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold text-slate-900">
                    {formatINR(c.offered_ctc)}
                  </p>
                  {c.ctc_source === 'fallback' && (
                    <p
                      className="text-[10px] text-amber-700 inline-flex items-center gap-0.5"
                      title="Offered CTC missing — using candidate's expected/current CTC"
                    >
                      <AlertCircle className="w-2.5 h-2.5" />
                      est.
                    </p>
                  )}
                  {c.ctc_source === 'missing' && (
                    <p className="text-[10px] text-slate-400">No CTC</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default ClosuresStrip;
